/**
 * 代管站「同期限改內容」（批151 裁示 99／109）的前端邏輯——`SiteContentModal` 用。
 *
 * ## 只送有改的欄
 *
 * 後端三欄都是「給了就**整包取代**」：沒改的那一欄也送回去，表面上值一樣，實際上是把編輯器
 * 正規化過的版本（例如 `QuotaEditor` 會把缺鍵補成 `absent_default`）蓋到證上——內容可能因此
 * 被判成偏離套裝、或在稽核裡多出一筆其實沒人動過的變更。
 *
 * 判準：開窗後三個編輯器**掛載完、各自回寫一次**之後拍一張基準照（`snapshotContent`），
 * 送出時只帶與基準不同的欄（`contentPatch`）。比的是「編輯器正規化後」對「編輯器正規化後」，
 * 所以掛載時的補值不會被誤認成操作者改過。
 */
import { knownErrorText } from '@/lib/apiErrors'
import { apiDetail } from '@/lib/siteSecrets'
import { hasTerm } from '@/lib/terms'

export interface ContentDraft {
  modules: Record<string, string>
  quotas: Record<string, number | null>
  policy: Record<string, unknown>
}

export type ContentField = keyof ContentDraft
export const CONTENT_FIELDS: ContentField[] = ['modules', 'quotas', 'policy']

/** 鍵排序後的 JSON（物件鍵順序不同不算改過）。 */
export function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value ?? null)
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  const obj = value as Record<string, unknown>
  return `{${Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`)
    .join(',')}}`
}

/** 基準照：三欄各自的 canonical 字串。 */
export function snapshotContent(draft: ContentDraft): Record<ContentField, string> {
  return {
    modules: canonical(draft.modules),
    quotas: canonical(draft.quotas),
    policy: canonical(draft.policy),
  }
}

/** 與基準照不同的欄（整欄送出）；沒有任何變動回空物件。 */
export function contentPatch(
  base: Record<ContentField, string>,
  draft: ContentDraft,
): Partial<ContentDraft> {
  const out: Partial<ContentDraft> = {}
  if (canonical(draft.modules) !== base.modules) out.modules = { ...draft.modules }
  if (canonical(draft.quotas) !== base.quotas) out.quotas = { ...draft.quotas }
  if (canonical(draft.policy) !== base.policy) out.policy = { ...draft.policy }
  return out
}

/** 證上的期限欄（`SiteLicense` 的子集合；`term_minutes` 在投影裡恆為數字，永久證是 0）。 */
export interface TermLike {
  expires_at: string | null
  term_days: number | null
  term_minutes: number | null | undefined
}

/**
 * 內容閘比期限時用的 `(term_days, term_minutes)`——後端 `issue.carry_over_expiry` 的逐列鏡像：
 *
 * | 證 | 回傳 |
 * |---|---|
 * | 已蓋章（`expires_at` 有值） | 兩欄 `null`＝不比（同期限，到期日原封不動） |
 * | 還沒蓋章、有期限 | 證上的兩欄（首次取證才起算，照證上的期限比） |
 * | 沒有期限（永久證） | 兩欄 `null`＝不比（後端回 `{expires_at: None}`，閘拿到的期限是 None） |
 *
 * ★ 覆核 🟡12：永久證的 `term_minutes` 是 `0` 不是 `null`。原寫法把它原樣送進閘 →
 *   `activation.given(0)` 為真 → 「期限 0 分鐘」不在任何選項裡 → 判偏離，admin 送不出去；
 *   後端同一張證卻不比期限、照常放行。判準一律走 `hasTerm`（總分鐘 > 0），不用 truthy。
 */
export function carriedTerm(lic: TermLike): { term_days: number | null; term_minutes: number | null } {
  if (lic.expires_at) return { term_days: null, term_minutes: null }
  if (!hasTerm(lic.term_days, lic.term_minutes)) return { term_days: null, term_minutes: null }
  return { term_days: lic.term_days, term_minutes: lic.term_minutes ?? null }
}

/** 欄名的中文（摘要與成功訊息用）。 */
export const CONTENT_FIELD_LABELS: Record<ContentField, string> = {
  modules: '模組',
  quotas: '配額',
  policy: '行為參數',
}

/**
 * 內容閘擋下的碼：後端定案（W1-D）一律 `role_required`＋`policy_reason`（`no_plan`／`custom_content`）；
 * 兩個舊碼留著認（升級過渡期的舊後端）。
 */
const GATE_CODES = ['role_required', 'activation_requires_approval', 'superadmin_required']

/** 同期限改內容的錯誤 → 中文一句話。 */
export function contentErrText(e: unknown, fallback = '改內容失敗'): string {
  const d = apiDetail(e)
  const code = d.code ?? ''
  if (GATE_CODES.includes(code)) {
    const label = typeof d.policy_label === 'string' && d.policy_label ? d.policy_label : '營運主管'
    if (d.policy_reason === 'no_plan') return `這張授權沒有對應的套裝（客製內容），需${label}。`
    return `內容偏離套裝，需${label}。`
  }
  if (code === 'no_change') return '內容與現行授權相同，不需要重簽。'
  if (code === 'content_required') return '模組、配額、行為參數至少要改一項。'
  if (code === 'site_not_live') return '站台已回收或回收中，不能再改授權內容。'
  if (code === 'no_active_license') return '這一站目前沒有生效中的授權（可能剛被別人改過），請重新整理後再試。'
  if (code === 'quotas_invalid') {
    const name = (typeof d.label === 'string' && d.label) || (typeof d.key === 'string' && d.key) || '其中一項'
    return d.reason === 'quota_not_nullable'
      ? `「${name}」不能設成「無上限」，請填一個數字。`
      : `請填寫配額「${name}」。`
  }
  // 批155：內部模組（`internal_plan_requires_manager`）等跨畫面共用的碼。
  const known = knownErrorText(d)
  if (known) return known
  if (typeof d.message === 'string' && d.message) return d.message
  return fallback
}
